/**
 * Typing a chord, with the parser answering as you go.
 *
 * `parseChordSymbol` is total and fast, so there is no reason to wait for a submit to tell
 * someone their symbol is unreadable — and every reason not to, because "invalid" arriving
 * after you have committed is the single most annoying pattern in form design. Here the
 * feedback line under the field is either the chord you just wrote, spelled out in notes,
 * or the parser's own account of where it gave up.
 *
 * Used by the correction sheet and by manual-entry mode, which is why it lives in
 * `system/` rather than inside either of them.
 */

import { useId, useMemo } from 'react';
import {
  isParseError,
  noteName,
  parseChordSymbol,
  respell,
  spellPitchClass,
  toCanonical,
  type CanonicalChord,
  type KeyContext,
} from '@hc/shared';
import { ChordSymbol } from './ChordSymbol.js';
import { MiniKeyboard } from './MiniKeyboard.js';

export interface ChordTextEntryProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired on Enter, and by whatever confirm button the parent renders. */
  onCommit: (chord: CanonicalChord) => void;
  keyContext: KeyContext;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
}

export interface ChordEntryReading {
  chord: CanonicalChord | null;
  message: string;
  valid: boolean;
}

/** The live reading of whatever is in the field. Exported so tests can assert on it. */
export function readChordEntry(input: string, keyContext: KeyContext): ChordEntryReading {
  const trimmed = input.trim();
  if (trimmed === '') return { chord: null, message: '', valid: false };

  const spec = parseChordSymbol(trimmed);
  if (isParseError(spec)) {
    return { chord: null, message: spec.message, valid: false };
  }

  const chord = respell(toCanonical(spec), keyContext);
  const notes = chord.pitchClasses
    .map((pc) => noteName(spellPitchClass(pc, keyContext)))
    .join(' · ');
  return { chord, message: notes, valid: true };
}

export function ChordTextEntry({
  value,
  onChange,
  onCommit,
  keyContext,
  label = 'Or type it',
  placeholder = 'e.g. Cm7/E♭',
  autoFocus = false,
}: ChordTextEntryProps): React.JSX.Element {
  const inputId = useId();
  const feedbackId = `${inputId}-feedback`;
  const reading = useMemo(() => readChordEntry(value, keyContext), [value, keyContext]);

  return (
    <div className="hc-entry">
      <label className="hc-entry__label" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        className="hc-entry__input"
        data-testid="hc-chord-input"
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        // The sheet exists to be typed into; landing the caret anywhere else would be a bug.
        autoFocus={autoFocus}
        value={value}
        placeholder={placeholder}
        aria-describedby={feedbackId}
        aria-invalid={value.trim() !== '' && !reading.valid}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && reading.chord) {
            event.preventDefault();
            onCommit(reading.chord);
          }
        }}
      />
      <p
        id={feedbackId}
        className={`hc-entry__feedback${reading.valid ? ' is-valid' : ''}`}
        data-testid="hc-chord-feedback"
        data-valid={reading.valid ? 'true' : 'false'}
        aria-live="polite"
      >
        {reading.chord ? (
          <>
            <MiniKeyboard
              pitchClasses={reading.chord.pitchClasses}
              bassPc={reading.chord.bassPc}
              label={`${reading.chord.symbol} notes`}
            />
            <span className="hc-entry__symbol">
              <ChordSymbol symbol={reading.chord.symbol} />
            </span>
            <span className="hc-entry__notes">{reading.message}</span>
          </>
        ) : (
          <span className="hc-entry__notes">{reading.message}</span>
        )}
      </p>
    </div>
  );
}
